import { useMemo } from 'react';
import { serialize } from 'next-mdx-remote/serialize';

import { DocActionsDrop } from 'components/doc/doc-actions-drop';
import { DocsBreadcrumbs } from 'components/docs/docs-breadcrumbs';
import { DocsContentSection } from 'components/docs/docs-content-section';
import { DocsTocSection } from 'components/docs/docs-toc-section';
import { DocsWrapper } from 'components/docs/docs-wrapper';
import meta from 'pages/_meta.json';
import { fetchArticle } from 'queries';
import { truncate } from 'utils';
import { baseOptions, extractHeadings } from 'utils/mdx';

const SNAPS_DOMAIN = process.env.NEXT_PUBLIC_SNAPS_CLIENT;

const DocsDetailPage = ({ ssr, router, session, store, toast }) => {
	const doc = ssr.doc?.data;
	const [category, categoryId] = router.query?.slug;
	const pages = meta.flatMap(section => section.pages);
	const pageIndex = pages.findIndex(page => page.href === `/${category}/${categoryId}`);
	const previous = pageIndex > 0 ? pages[pageIndex - 1] : undefined;
	const next = pageIndex >= 0 ? pages[pageIndex + 1] : undefined;

	// Computed
	const photo = useMemo(() => {
		const baseUrl = new URL(`${SNAPS_DOMAIN}/api/image`);
		baseUrl.searchParams.set('template', 'docs');
		baseUrl.searchParams.set('title', doc.title);
		baseUrl.searchParams.set('subtitle', truncate(doc.description, 56));
		baseUrl.searchParams.set('format', 'png');
		return baseUrl.toString();
	}, []);

	// Render
	return (
		<DocsWrapper seo={{ type: 'article', photo, description: doc.description }}>
			<div className='grid min-w-0 xl:grid-cols-[minmax(0,1fr)_14rem]'>
				<div className='min-w-0 px-5 py-10 sm:px-8 lg:py-14'>
					<div className='mx-auto max-w-3xl'>
						<div className='mb-8 flex-between gap-4 border-b pb-5'>
							<DocsBreadcrumbs className='mb-0' />
							<DocActionsDrop />
						</div>
						<DocsContentSection doc={doc} previous={previous} next={next} />
					</div>
				</div>
				<aside className='sticky top-[var(--docs-header-offset)] hidden h-[calc(100dvh-var(--docs-header-offset))] overflow-y-auto border-l px-6 py-14 xl:block'>
					<DocsTocSection headings={doc.headings || []} />
				</aside>
			</div>
		</DocsWrapper>
	);
};

const REDIRECTS = {
	intro: '/intro/welcome',
	concepts: '/concepts/accounts',
	cli: '/cli/overview',
	mcp: '/mcp/overview',
	reference: '/reference/overview',
	guides: '/guides/overview',
	faqs: '/intro/welcome',
};

const PAGE_REDIRECTS = {
	'cli/usage': '/cli/quickstart',
	'faqs/overview': '/intro/welcome',
	'faqs/getting-started': '/intro/quickstart',
	'faqs/community': '/intro/guidelines',
	'faqs/marketplace': '/concepts/templates',
	'faqs/pricing': '/concepts/subscriptions',
	'faqs/accounts': '/concepts/accounts',
	'faqs/bounties': '/intro/welcome',
	'faqs/collections': '/concepts/library',
	'faqs/templates': '/concepts/templates',
};

export async function getServerSideProps({ params, res }) {
	try {
		const [category, categoryId] = params?.slug;
		const isMarkdownRoute = categoryId?.endsWith('.md');
		const normalizedCategoryId = isMarkdownRoute ? categoryId.replace(/\.md$/, '') : categoryId;
		const pageKey = `${category}/${normalizedCategoryId}`;

		if (PAGE_REDIRECTS[pageKey]) {
			const destination = isMarkdownRoute ? `${PAGE_REDIRECTS[pageKey]}.md` : PAGE_REDIRECTS[pageKey];
			return { redirect: { destination, permanent: true } };
		}

		if (isMarkdownRoute) {
			const doc = await fetchArticle(category, normalizedCategoryId);
			res.setHeader('Content-Type', 'text/markdown; charset=utf-8');
			res.write(doc.content);
			res.end();

			return { props: {} };
		}

		if (REDIRECTS[category] && !categoryId) {
			return { redirect: { destination: REDIRECTS[category], permanent: true } };
		}
		const doc = await fetchArticle(category, categoryId);
		const content = await serialize(doc.content, { mdxOptions: baseOptions as any });
		const headings = extractHeadings(doc.content);
		return { props: { doc: { data: { ...doc, content, headings } } } };
	} catch (e) {
		console.error(e);
		return { notFound: true };
	}
}

export default DocsDetailPage;
